<template>
  <div class="map-com" ref="mapCom">
    <div class="map-tools">
      <a-popover placement="right" trigger="hover" v-model="popoverVisible">
        <div slot="content">
          <p>显示内容：</p>
          名称：<a-switch defaultChecked @change="onChange(arguments[0], 'name')" /><br>
          备注：<a-switch defaultChecked @change="onChange(arguments[0], 'label')"/><br>
          卫星：<a-switch @change="onChange(arguments[0], 'satellite')"/>
          <a-radio-group
            v-if="satelliteOn"
            size="small"
            :value="satelliteType"
            @change="handleSatelliteTypeChange"
            style="margin-left: 8px;"
          >
            <a-radio value="amap">高德</a-radio>
            <a-radio value="tianditu">天地图</a-radio>
          </a-radio-group><br>
          路况：<a-switch @change="onChange(arguments[0], 'traffic')"/><br>
        </div>
        <a-button
          class="map-tool"
          shape="circle"
          @click="handleChangeVisible">
          <i class="iconfont icon-xianshishezhi"></i>
        </a-button>
      </a-popover>

      <a-tooltip placement="right">
        <template slot="title">
          <span>测距</span>
        </template>
        <a-button
          class="map-tool"
          :class="[rulerOn ? 'btn-active' : '']"
          shape="circle"
          @click="handleCeju">
          <i class="iconfont icon-ceju"></i>
        </a-button>
      </a-tooltip>
      <a-button
        class="map-tool"
        shape="circle"
        @click="handleFullScreen">
        <i class="iconfont" :class="isFullScreen ? 'icon-tuichuquanping1': 'icon-quanping2'"></i>
      </a-button>
    </div>

    <div
      id="mapContainer"
      :class="[isAddingMarker ? 'adding-marker' : '', drawModeActive ? 'drawing-polygon' : '']"
      @contextmenu.prevent="handleRightClickMap"
    >
    </div>

    <a-alert v-if="isAddingMarker" show-icon class="fast-add-tips" message="已开启快速添加模式" type="info">
      <span type="link" slot="closeText" @click.stop="isAddingMarker = false">
        关闭
      </span>
    </a-alert>

    <!-- 面绘制工具栏 -->
    <polygon-draw-toolbar
      :visible="drawModeActive"
      :pointCount="drawPointCount"
      :drawColorKey="drawColorKey"
    />

  </div>
</template>

<script>
/* global AMap */
import axios from 'axios';
import config from '@/config/index';
import {
  getPolygonStyle, pointsToPath, getPolygonCenter, POLYGON_DEFAULT_COLOR_KEY,
} from '@/utils/polygon-utils';
import { gcj02ToWgs84, wgs84ToGcj02 } from '@/utils/coord-transform';
import PolygonDrawToolbar from '@/components/polygon-draw-toolbar/index.vue';

export default {
  components: {
    PolygonDrawToolbar,
  },
  data() {
    return {
      map: {},
      markerList: [],
      mapMarkerList: [],
      polyline: [],
      lastHighlight: null,
      popoverVisible: false,

      markerSet: {},
      isAddingMarker: false,

      ruler: {},
      rulerOn: false,
      satelliteOn: false,
      trafficOn: false,
      satelliteType: 'amap', // 'amap' | 'tianditu'
      isFullScreen: false, // 添加全屏状态标记
      // 天地图 API Key 池（与小程序端一致）
      tdtKeyPool: [
        '01fc392530bd93c10dadb999475c3632',
        'f423193014cde7bf44b224c6ab2b1210',
        'e8a16137fd226a62a23cc7ba5c9c78ce',
        '1eb44fae5b9dc454442b322e9a41d233',
      ],
      tdtKeyIndex: 0,

      // 面标记相关
      polygonOverlays: [], // AMap.Polygon 实例数组
      lastRenderedPolygons: [], // 最后一次渲染的面列表
      lastHighlightPolygon: null,
      // 绘制模式
      drawModeActive: false,
      drawPointCount: 0,
      drawColorKey: POLYGON_DEFAULT_COLOR_KEY,
      tempDrawPolygon: null, // 绘制中的临时面
      tempDrawMarkers: [], // 绘制中的顶点标记
    };
  },
  methods: {
    // 地图初始化函数
    initMap() {
      this.map = new AMap.Map('mapContainer');
      this.map.on('moveend', () => {
        const center = this.map.getCenter();
        this.$bus.$emit('centerChange', `${center.getLng()},${center.getLat()}`);
      });

      // 绑定事件
      this.map.on('click', this.handleMapClick);

      // 加载距离测量插件
      this.map.plugin(['AMap.RangingTool'], () => {
        this.ruler = new AMap.RangingTool(this.map);
      });
    },
    async handleMapClick(event) {
      const lng = event.lnglat.getLng();
      const lat = event.lnglat.getLat();
      // 绘制面标记模式：点击地图直接添加点
      if (this.drawModeActive) {
        // 天地图模式下高德返回 WGS-84，转回 GCJ-02 存储
        const gcj = this.toGcj02(lng, lat);
        this.$bus.$emit('drawAddPointResult', { latitude: gcj.lat, longitude: gcj.lng });
        return;
      }
      // 点击地图空白处：取消选中
      this.resetPolygonHighlight();
      this.resetMarkerHighlight();
      // 快速添加标记模式
      if (!this.isAddingMarker) return;
      // 获取标记信息并标点
      const res = await axios.post(`${config.mapServerBaseURL}/location/regeocode`, {
        longitude: lng,
        latitude: lat,
      });
      if (res.data.formatted_address) {
        const gcj = this.toGcj02(lng, lat);
        this.$bus.$emit('markerAdd', {
          name: res.data.formatted_address,
          address: res.data.formatted_address,
          longitude: gcj.lng,
          latitude: gcj.lat,
        });
      } else {
        this.$message.error('获取不到该地址信息');
      }
    },
    // ========== 坐标转换（天地图模式下启用） ==========
    // GCJ-02 → WGS-84（渲染时用）
    toWgs84(lng, lat) {
      return this.isTiandituMode ? gcj02ToWgs84(lng, lat) : { lng, lat };
    },
    // WGS-84 → GCJ-02（存储时用）
    toGcj02(lng, lat) {
      return this.isTiandituMode ? wgs84ToGcj02(lng, lat) : { lng, lat };
    },
    getMarkerIconName(iconPath) {
      const iconName = iconPath.replace('/common/img/', '');
      return iconName;
    },
    getMapMarkerList() {
      const mapMarkerList = [];
      this.markerList.forEach((item) => {
        const markerIconName = this.getMarkerIconName(item.iconPath);
        const content = `<pre class="marker-name">${item.name}</pre><img src="${`/img/${markerIconName}`}"></img>`;
        const pos = this.toWgs84(item.longitude, item.latitude);
        const mapMarker = new AMap.Marker({
          content, // 自定义点标记覆盖物内容
          position: [pos.lng, pos.lat],
          offset: new AMap.Pixel(-13, -26),
          clickable: true,
          // anchor: 'bottom-center',
          topWhenClick: true,
        });
        if (item.labelContent) {
          mapMarker.setLabel({
            // offset: new AMap.Pixel(13, 26),
            content: `<pre class="marker-label">${item.labelContent}</pre>`,
          });
        } else if (item.label) {
          mapMarker.setLabel({
            // offset: new AMap.Pixel(13, 26),
            content: `<pre class="marker-label">${item.label.content}</pre>`,
          });
        }
        mapMarker.setExtData(item);
        mapMarker.on('click', this.handleMarkerClick);
        mapMarkerList.push(mapMarker);
      });
      return mapMarkerList;
    },
    renderPolyline() {
      this.map.remove(this.polyline);
      const path = this.markerList.map((item) => {
        const pos = this.toWgs84(item.longitude, item.latitude);
        return [pos.lng, pos.lat];
      });
      this.polyline = new AMap.Polyline({
        path,
        isOutline: true,
        outlineColor: '#ffeeff',
        borderWeight: 3,
        strokeColor: 'red',
        strokeOpacity: 1,
        strokeWeight: 6,
        // 折线样式还支持 'dashed'
        strokeStyle: 'solid',
        // strokeStyle是dashed时有效
        strokeDasharray: [10, 5],
        lineJoin: 'round',
        lineCap: 'round',
        zIndex: 50,
        showDir: true,
      });
      this.map.add(this.polyline);
    },
    // 被父组件调用
    renderMapMarker(markerList, disableFit) {
      // 先移除
      this.map.remove(this.mapMarkerList);

      this.markerList = markerList;
      this.mapMarkerList = this.getMapMarkerList();
      this.map.add(this.mapMarkerList);

      // 缩放以显示所有点
      if (!disableFit) {
        this.map.setFitView();
      }

      // 更新连线
      if (this.markerSet.showPolyline) {
        this.renderPolyline();
      } else {
        this.map.remove(this.polyline);
      }
    },
    // ========== 面标记渲染 ==========
    // 被父组件调用：渲染面标记
    renderPolygons(polygonList, disableFit) {
      // 保存最后一次渲染的面列表（用于卫星模式切换时重新渲染）
      this.lastRenderedPolygons = polygonList || [];
      // 先移除旧面
      this.map.remove(this.polygonOverlays);
      this.polygonOverlays = [];

      if (!polygonList || polygonList.length === 0) return;

      polygonList.forEach((polygon) => {
        const style = getPolygonStyle(polygon.colorKey || POLYGON_DEFAULT_COLOR_KEY);
        // 天地图模式下将 GCJ-02 坐标转为 WGS-84
        const path = pointsToPath(polygon.points).map(([lng, lat]) => {
          const pos = this.toWgs84(lng, lat);
          return [pos.lng, pos.lat];
        });
        if (path.length < 3) return;

        const amapPolygon = new AMap.Polygon({
          path,
          strokeColor: style.strokeColor,
          strokeWidth: style.strokeWidth,
          fillColor: style.fillColor,
          zIndex: 10,
        });
        amapPolygon.setExtData(polygon);
        amapPolygon.on('click', this.handlePolygonClick);
        this.polygonOverlays.push(amapPolygon);
      });

      this.map.add(this.polygonOverlays);

      if (!disableFit) {
        this.map.setFitView();
      }
    },
    handlePolygonClick(event) {
      const amapPolygon = event.target;
      const polygon = amapPolygon.getExtData();
      // 高亮被点击的面（不居中）
      this.setPolygonHighlight(polygon, false);
      this.$emit('polygonClick', polygon);
    },
    // 面标记高亮
    setPolygonHighlight(polygon, needCenter = true) {
      // 先取消上一个高亮
      this.resetPolygonHighlight();

      const found = this.polygonOverlays.find(p => p.getExtData().id === polygon.id);
      if (found) {
        const extData = found.getExtData();
        const style = getPolygonStyle(extData.colorKey || POLYGON_DEFAULT_COLOR_KEY);
        const hex = style.strokeColor.slice(0, 7); // 去掉透明度
        found.setOptions({
          strokeColor: `${hex}FF`,
          strokeWidth: 3,
          fillColor: `${hex}66`,
          zIndex: 100,
        });
        this.lastHighlightPolygon = found;

        // 居中到面中心（列表点击时需要，地图直接点击时不需要）
        if (needCenter) {
          const center = getPolygonCenter(extData.points);
          if (center) {
            const pos = this.toWgs84(center.longitude, center.latitude);
            this.map.setCenter(new AMap.LngLat(pos.lng, pos.lat));
          }
        }
      }
    },
    resetPolygonHighlight() {
      if (this.lastHighlightPolygon) {
        const extData = this.lastHighlightPolygon.getExtData();
        const style = getPolygonStyle(extData.colorKey || POLYGON_DEFAULT_COLOR_KEY);
        this.lastHighlightPolygon.setOptions({
          strokeColor: style.strokeColor,
          strokeWidth: style.strokeWidth,
          fillColor: style.fillColor,
          zIndex: 10,
        });
        this.lastHighlightPolygon = null;
      }
    },
    // ========== 绘制模式 ==========
    // 进入绘制模式
    startDrawMode(initialColorKey) {
      this.drawModeActive = true;
      this.drawPointCount = 0;
      this.drawColorKey = initialColorKey || POLYGON_DEFAULT_COLOR_KEY;
      this.tempDrawPolygon = null;
      this.tempDrawMarkers = [];
    },
    // 退出绘制模式
    endDrawMode() {
      this.drawModeActive = false;
      this.drawPointCount = 0;
      // 清除临时面
      if (this.tempDrawPolygon) {
        this.map.remove(this.tempDrawPolygon);
        this.tempDrawPolygon = null;
      }
      // 清除顶点标记
      if (this.tempDrawMarkers.length) {
        this.map.remove(this.tempDrawMarkers);
        this.tempDrawMarkers = [];
      }
    },
    // 更新绘制中的临时面
    updateDrawPolygon(drawPoints, drawColorKey) {
      this.drawPointCount = drawPoints.length;
      this.drawColorKey = drawColorKey || POLYGON_DEFAULT_COLOR_KEY;

      // 移除旧的临时面并置 null
      if (this.tempDrawPolygon) {
        this.map.remove(this.tempDrawPolygon);
        this.tempDrawPolygon = null;
      }
      // 移除旧的顶点标记
      if (this.tempDrawMarkers.length) {
        this.map.remove(this.tempDrawMarkers);
        this.tempDrawMarkers = [];
      }

      if (drawPoints.length === 0) return;

      const style = getPolygonStyle(this.drawColorKey);
      // 天地图模式下将 GCJ-02 坐标转为 WGS-84 渲染
      const path = pointsToPath(drawPoints).map(([lng, lat]) => {
        const pos = this.toWgs84(lng, lat);
        return [pos.lng, pos.lat];
      });

      // 绘制顶点标记（锚点图标，与小程序端一致）
      this.tempDrawMarkers = drawPoints.map((p) => {
        const pos = this.toWgs84(p.longitude, p.latitude);
        const marker = new AMap.Marker({
          position: [pos.lng, pos.lat],
          content: '<img src="/img/maodian.png" style="width:20px;height:20px;transform:translate(-10px,-10px);">',
          offset: new AMap.Pixel(0, 0),
          zIndex: 300,
        });
        return marker;
      });
      this.map.add(this.tempDrawMarkers);

      // 绘制临时面（至少3个点画面，使用与已保存面相同的样式）
      if (path.length >= 3) {
        this.tempDrawPolygon = new AMap.Polygon({
          path,
          strokeColor: style.strokeColor,
          strokeWidth: style.strokeWidth,
          fillColor: style.fillColor,
          zIndex: 200,
        });
        this.map.add(this.tempDrawPolygon);
      }
    },
    setCenter(marker) {
      // 高亮点标记（不居中）
      this.setMarkerHighlight(marker);
      // 居中
      const pos = this.toWgs84(marker.longitude, marker.latitude);
      this.map.setCenter(new AMap.LngLat(pos.lng, pos.lat));
    },
    // 点标记高亮
    setMarkerHighlight(marker) {
      // 取消上一个高亮
      this.resetMarkerHighlight();
      // 高亮当前
      const foundMarker = this.mapMarkerList.find(item => item.getExtData().id === marker.id);
      if (foundMarker) {
        const extData = foundMarker.getExtData();
        const markerIconName = this.getMarkerIconName(extData.iconPath);
        const content = `<pre class="marker-name highlight">${extData.name}</pre><img src="${`/img/${markerIconName}`}"/>`;
        foundMarker.setContent(content);
        if (extData.label) {
          foundMarker.setLabel({
            content: `<pre class="marker-label highlight">${extData.label.content}</pre>`,
          });
        }
        foundMarker.setzIndex(200);
        this.lastHighlight = foundMarker;
      }
    },
    // 取消点标记高亮
    resetMarkerHighlight() {
      if (this.lastHighlight) {
        const foundMarker = this.lastHighlight;
        const extData = foundMarker.getExtData();
        const markerIconName = this.getMarkerIconName(extData.iconPath);
        const content = `<pre class="marker-name">${extData.name}</pre><img src="${`/img/${markerIconName}`}"/>`;
        foundMarker.setContent(content);
        if (extData.label) {
          foundMarker.setLabel({
            content: `<pre class="marker-label">${extData.label.content}</pre>`,
          });
        }
        foundMarker.setzIndex(100);
        this.lastHighlight = null;
      }
    },
    handleMarkerClick(event) {
      const mapMarker = event.target;
      const marker = mapMarker.getExtData();
      // 高亮被点击的点标记（不居中）
      this.setMarkerHighlight(marker);
      this.$emit('mapMarkerClick', marker);
    },
    handleChangeVisible() {
      this.popoverVisible = true;
    },
    onChange(value, which) {
      if (which === 'name') {
        if (value) {
          this.mapMarkerList.forEach((item) => {
            const extData = item.getExtData();
            const markerIconName = this.getMarkerIconName(extData.iconPath);
            const content = `<pre class="marker-name">${extData.name}</pre><img src="${`/img/${markerIconName}`}"/>`;
            item.setContent(content);
            // item.setOffset(new AMap.Pixel(-13, -51));
          });
        } else {
          this.mapMarkerList.forEach((item) => {
            const extData = item.getExtData();
            const markerIconName = this.getMarkerIconName(extData.iconPath);
            item.setContent(`<img src="${`/img/${markerIconName}`}"/>`);
            // item.setOffset(new AMap.Pixel(-13, -26));
          });
        }
      } else if (which === 'label') {
        if (value) {
          this.mapMarkerList.forEach((item) => {
            const extData = item.getExtData();
            let labelContent;
            if (extData.labelContent) {
              labelContent = `<pre class="marker-label">${extData.labelContent}</pre>`;
            } else {
              labelContent = '';
            }
            item.setLabel({
              // offset: new AMap.Pixel(13, 26),
              content: labelContent,
            });
          });
        } else {
          this.mapMarkerList.forEach((item) => {
            item.setLabel('');
          });
        }
      } else if (which === 'satellite') {
        this.satelliteOn = !this.satelliteOn;
        this.refreshAll();
      } else if (which === 'traffic') {
        this.trafficOn = !this.trafficOn;
        this.refreshLayers();
      }
    },
    refreshLayers() {
      const layers = [];
      if (this.isTiandituMode) {
        // 天地图卫星：直接用标准瓦片坐标请求，不叠加高德底图
        // 高德 tileUrl 的 x,y,z 是标准 Web Mercator 坐标（WGS-84）
        const key = this.tdtKeyPool[this.tdtKeyIndex];
        layers.push(new AMap.TileLayer({
          tileUrl: (x, y, z) => {
            const sub = x % 8;
            return `https://t${sub}.tianditu.gov.cn/DataServer`
              + `?T=img_w&x=${x}&y=${y}&l=${z}&tk=${key}`;
          },
          tileSize: 256,
          zIndex: 1,
        }));
        // 天地图影像注记（路网、地名）
        layers.push(new AMap.TileLayer({
          tileUrl: (x, y, z) => {
            const sub = x % 8;
            return `https://t${sub}.tianditu.gov.cn/DataServer`
              + `?T=cia_w&x=${x}&y=${y}&l=${z}&tk=${key}`;
          },
          tileSize: 256,
          zIndex: 2,
        }));
      } else {
        // 高德默认底图
        layers.push(new AMap.TileLayer());
        if (this.satelliteOn) {
          // 高德卫星
          layers.push(new AMap.TileLayer.Satellite());
        }
      }
      if (this.trafficOn) {
        // 实时交通图
        layers.push(new AMap.TileLayer.Traffic());
      }
      this.map.setLayers(layers);
    },
    // 卫星图层类型切换
    handleSatelliteTypeChange(e) {
      this.satelliteType = e.target.value;
      this.refreshAll();
    },
    // 刷新图层 + 重新渲染覆盖物（卫星模式切换时调用）
    refreshAll() {
      this.refreshLayers();
      this.renderMapMarker(this.markerList, true);
      this.renderPolygons(this.lastRenderedPolygons, true);
    },
    handleRightClickMap() {
      this.isAddingMarker = false;
    },
    handleCeju() {
      this.rulerOn = !this.rulerOn;
      if (this.rulerOn) {
        this.ruler.turnOn();
      } else {
        this.ruler.turnOff();
      }
    },
    handleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
      const { mapCom } = this.$refs;
      if (this.isFullScreen) {
        if (mapCom.requestFullscreen) {
          mapCom.requestFullscreen();
        }
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    },
  },
  computed: {
    // 是否天地图模式（影响坐标转换）
    isTiandituMode() {
      return this.satelliteOn && this.satelliteType === 'tianditu';
    },
  },
  mounted() {
    this.initMap();
    this.$bus.$on('renderMarkerSet', (markerSet) => {
      this.markerSet = markerSet;
      // 应用显示配置（卫星图、路况等）
      const opts = markerSet.showOptions || {};
      this.satelliteOn = !!opts.satellite;
      this.satelliteType = opts.satelliteType === 'tianditu' ? 'tianditu' : 'amap';
      this.trafficOn = !!opts.traffic;
      this.refreshLayers();
      if (markerSet.showPolyline) {
        this.renderPolyline();
      } else {
        this.map.remove(this.polyline);
      }
    });
    this.$bus.$on('prepareAddMarker', () => {
      this.isAddingMarker = !this.isAddingMarker;
    });
    // 绘制模式事件
    this.$bus.$on('startDrawPolygon', this.startDrawMode);
    this.$bus.$on('drawPointsUpdate', this.updateDrawPolygon);
    this.$bus.$on('endDrawPolygon', this.endDrawMode);
    // 取消高亮事件
    this.$bus.$on('resetMarkerHighlight', this.resetMarkerHighlight);
    this.$bus.$on('resetPolygonHighlight', this.resetPolygonHighlight);
  },
  destroyed() {
    this.$bus.$off('renderMarkerSet');
    this.$bus.$off('prepareAddMarker');
    this.$bus.$off('startDrawPolygon', this.startDrawMode);
    this.$bus.$off('drawPointsUpdate', this.updateDrawPolygon);
    this.$bus.$off('endDrawPolygon', this.endDrawMode);
    this.$bus.$off('resetMarkerHighlight', this.resetMarkerHighlight);
    this.$bus.$off('resetPolygonHighlight', this.resetPolygonHighlight);
  },
};
</script>

<style scoped>
#mapContainer {
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.fast-add-tips {
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9;
  width: 300px;
}

* /deep/ [type=button] {
  -webkit-appearance: none;
}
</style>

<style>
.map-com {
  position: relative;
}

.map-tools {
  background: #ffffffcc;
  position: absolute;
  left: 20px;
  bottom: 30px;
  z-index: 999;
  padding: 0 0 16px 0;
  width: 50px;
  text-align: center;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
}

.map-tool {
  display: inline-block;
  margin-top: 16px;
  opacity: 1;
}

.btn-active{
  color: #1890ff !important;
  background-color: #096dd9 !important;
  border-color: #096dd9 !important;
}

.marker-name,
.marker-label {
  font-family: "Chinese Quote", -apple-system, BlinkMacSystemFont, "Segoe UI",
    "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei",
    "Helvetica Neue", Helvetica, Arial, sans-serif,
    "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-size: 14px;
  text-align: center;
}

.marker-name {
  margin: 0;
  transform: translateX(-50%);
  position: relative;
  left: 13px;
  white-space: pre;
  background-color: #000000;
  border: 2px solid #ffffff;
  border-radius: 4px;
  color: #ffffff;
  padding: 0 4px;
  position: absolute;
  bottom: 100%;
}

.marker-name.highlight,
.marker-label.highlight {
  border: 2px solid #09bb07;
}

.marker-label {
  margin: 0;
  background-color: #ffffff;
  border: 2px solid #000000;
  border-radius: 4px;
  color: #000000;
  padding: 2px 4px;
  position: absolute;
  top: 26px;
  left: 13px;
  white-space: pre;
}

.amap-marker-label {
    border: none;
    background-color: transparent;
}

.adding-marker {
  cursor: crosshair !important;
}

.drawing-polygon {
  cursor: crosshair !important;
}
</style>
